import { ArrowUpRight, ArrowDownRight, BarChart3 } from "lucide-react"
import {
    Label,
    PolarGrid,
    PolarRadiusAxis,
    RadialBar,
    RadialBarChart,
} from "recharts"

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "./card"
import {
    ChartContainer,
} from "./chart"

export const description = "A radial chart showing total sales performance"

export function ChartRadialShape({
    netSale,
    growthNet,
    totalOrders,
    targetSales = null
}) {
    let progressPercentage = 0
    let progressLabel = "Growth"

    if (targetSales && targetSales > 0) {
        progressPercentage = Math.min((netSale / targetSales) * 100, 100)
        progressLabel = "of Target"
    } else {
        progressPercentage = Math.min(50 + (growthNet * 5), 100)
        progressPercentage = Math.max(progressPercentage, 0)
    }

    const chartData = [
        { metric: "sales", value: progressPercentage, fill: "#017971" },
    ]

    const chartConfig = {
        sales: {
            label: "Sales",
        },
        net: {
            label: "Net Sales",
            color: "#017971",
        },
    }

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0'

        const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : amount

        const formatted = new Intl.NumberFormat('en-PK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num)

        return `${formatted}`
    }

    return (
        <Card className="relative overflow-hidden flex flex-col">
            <div className="absolute right-0 top-0 h-16 w-16 bg-primary/10 rounded-full -translate-y-8 translate-x-8" />
            <CardHeader className="items-center pb-0">
                <div className="flex items-center justify-between w-full">
                    <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </div>
                <CardDescription>Showing performance metrics</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-0">
                <ChartContainer
                    config={chartConfig}
                    className="mx-auto aspect-square max-h-[250px]"
                >
                    <RadialBarChart
                        data={chartData}
                        startAngle={90}
                        endAngle={90 + (360 * (progressPercentage / 100))}
                        innerRadius={80}
                        outerRadius={140}
                    >
                        <PolarGrid
                            gridType="circle"
                            radialLines={false}
                            stroke="none"
                            className="first:fill-muted last:fill-background"
                            polarRadius={[86, 74]}
                        />
                        <RadialBar
                            dataKey="value"
                            background
                            cornerRadius={10}
                        />
                        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                            <Label
                                content={({ viewBox }) => {
                                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                        return (
                                            <text
                                                x={viewBox.cx}
                                                y={viewBox.cy}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                            >
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={viewBox.cy}
                                                    className="fill-foreground text-4xl font-bold"
                                                >
                                                    {formatCurrency(netSale)}
                                                </tspan>
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={(viewBox.cy || 0) + 24}
                                                    className="fill-muted-foreground"
                                                >
                                                    Net Sales
                                                </tspan>
                                            </text>
                                        )
                                    }
                                }}
                            />
                        </PolarRadiusAxis>
                    </RadialBarChart>
                </ChartContainer>
            </CardContent>
            <CardFooter className="flex-col gap-2 text-sm pb-6">
                <div className="flex items-center justify-center gap-2 text-sm">
                    {growthNet > 0 ? (
                        <>
                            <ArrowUpRight className="h-4 w-4 text-green-500" />
                            <span className="text-green-500 font-medium">{growthNet.toFixed(1)}%</span>
                        </>
                    ) : (
                        <>
                            <ArrowDownRight className="h-4 w-4 text-red-500" />
                            <span className="text-red-500 font-medium">{Math.abs(growthNet).toFixed(1)}%</span>
                        </>
                    )}
                    <span className="text-muted-foreground">vs yesterday</span>
                </div>
                <div className="text-center text-muted-foreground leading-none">
                    {totalOrders} orders • {progressPercentage.toFixed(0)}% {progressLabel}
                </div>
            </CardFooter>
        </Card>
    )
}